import { TagIcon } from 'lucide-react';
import { Badge } from '@/shared/components/ui/badge';
import { cn } from '@/shared/lib/utils';

/** Formata um valor numérico como moeda brasileira (BRL). */
function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

interface PromotionBadgeProps {
  className?: string;
  /**
   * Percentual de desconto (De/Por) a destacar no selo, ex.: `20` → "-20% OFF".
   * Quando ausente ou não positivo, o selo cai para o rótulo textual `label`.
   */
  percent?: number | null;
  /** Rótulo exibido quando não há percentual. Padrão: "Promoção". */
  label?: string;
}

/**
 * Selo de promoção (De/Por) exibido nos itens em oferta. Quando o percentual de
 * desconto é conhecido, ele é destacado ("-20% OFF") com um ícone de etiqueta;
 * caso contrário, exibe o rótulo textual.
 *
 * O conteúdo é renderizado como texto puro (React escapa o conteúdo) e o
 * percentual é sempre coagido a inteiro dentro de 1–99, evitando injeção ou
 * valores absurdos a partir de dados eventualmente controlados pelo backend.
 */
export function PromotionBadge({ className, percent, label = 'Promoção' }: PromotionBadgeProps) {
  const safePercent =
    typeof percent === 'number' && Number.isFinite(percent) && percent > 0
      ? Math.min(Math.round(percent), 99)
      : null;

  return (
    <Badge
      variant="destructive"
      className={cn(
        'gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide shadow-sm',
        className
      )}
    >
      <TagIcon aria-hidden strokeWidth={2.5} />
      {safePercent !== null ? `-${safePercent}% OFF` : label}
    </Badge>
  );
}

interface PromotionPriceProps {
  /** Preço atual exibido em destaque ("Por"). */
  price: number;
  /**
   * Preço cheio ("De") exibido riscado. Quando informado e maior que `price`,
   * ativa a apresentação De/Por; caso contrário, apenas o preço atual é exibido.
   */
  regularPrice?: number | null;
  /** Texto opcional exibido acima do preço (ex.: "A partir de"). */
  prefix?: string;
  className?: string;
  priceClassName?: string;
  /** Alinha o conteúdo à direita (usado no cabeçalho do drawer). */
  align?: 'left' | 'right';
  /**
   * Percentual de desconto mostrado num selo verde ao lado do preço "De"
   * (PRAT-110). Opcional: sem ele, só o preço riscado.
   */
  percent?: number | null;
}

/**
 * Exibe o preço de um produto com suporte à apresentação "De/Por" quando há
 * promoção: o preço cheio aparece riscado e o preço de venda em destaque.
 */
export function PromotionPrice({
  price,
  regularPrice,
  prefix,
  className,
  priceClassName,
  align = 'left',
  percent,
}: PromotionPriceProps) {
  const isPromo = typeof regularPrice === 'number' && regularPrice > price;
  const safePercent =
    isPromo && typeof percent === 'number' && Number.isFinite(percent) && percent > 0
      ? Math.min(Math.round(percent), 99)
      : null;

  return (
    <div className={cn('flex flex-col', align === 'right' && 'items-end', className)}>
      {prefix && <span className="text-xs text-gray-500">{prefix}</span>}
      {isPromo && (
        <span className="flex items-center gap-1.5">
          <span className="text-xs text-gray-400 line-through">{formatBRL(regularPrice!)}</span>
          {safePercent !== null && (
            <span className="rounded-full bg-emerald-600 px-1.5 py-px text-[10px] font-bold text-white">
              -{safePercent}%
            </span>
          )}
        </span>
      )}
      <p
        className={cn(
          'text-sm sm:text-base font-bold',
          isPromo ? 'text-primary' : 'text-night',
          priceClassName
        )}
      >
        {formatBRL(price)}
      </p>
    </div>
  );
}
